import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Globe, MapPin, Building2, ChevronRight, Package } from 'lucide-react';
import { getRegions, getRegionData } from '../services/api';

const regionFlags = {
  'US': '🇺🇸', 'EU': '🇪🇺', 'UK': '🇬🇧', 'IN': '🇮🇳', 'AE': '🇦🇪',
  'JP': '🇯🇵', 'CN': '🇨🇳', 'BR': '🇧🇷', 'ZA': '🇿🇦', 'AU': '🇦🇺'
};

function RegionCard({ region, selected, onClick }) {
  return (
    <button
      onClick={() => onClick(region.code)}
      className={`p-4 rounded-xl border-2 text-left transition-all ${
        selected 
          ? 'border-[#0A2540] bg-[#0A2540]/5' 
          : 'border-slate-200 hover:border-slate-300'
      }`}
      data-testid={`region-${region.code}`}
    >
      <div className="flex items-center gap-3 mb-2">
        <span className="text-2xl">{regionFlags[region.code] || '🌍'}</span>
        <div>
          <p className={`font-semibold ${selected ? 'text-[#0A2540]' : 'text-slate-900'}`}>
            {region.name}
          </p>
          <p className="text-xs text-slate-500">{region.regulatory_body}</p>
        </div>
      </div>
    </button>
  );
}

function GenericRow({ generic }) {
  const getConfidenceColor = (confidence) => {
    if (confidence >= 85) return 'bg-emerald-500';
    if (confidence >= 70) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="data-table-row flex items-center py-3 px-4" data-testid={`generic-row-${generic.id}`}>
      <div className="flex-1">
        <p className="font-medium text-slate-900">{generic.name}</p>
        <p className="text-sm text-slate-500">{generic.manufacturer}</p>
      </div>
      <div className="w-24 px-4">
        <span className="badge-neutral">{generic.dosage_form}</span>
      </div>
      <div className="w-24 px-4 text-sm text-slate-700">
        {generic.strength}
      </div>
      <div className="w-32 px-4">
        <div className="score-bar">
          <div 
            className={`score-fill ${getConfidenceColor(generic.confidence)}`}
            style={{ width: `${generic.confidence}%` }}
          />
        </div>
        <span className="text-xs text-slate-500">{generic.confidence}%</span>
      </div>
      <div className="w-24 text-right">
        <span className={`${
          generic.review_status === 'recommended' ? 'badge-success' :
          generic.review_status === 'pharmacist_review' ? 'badge-warning' :
          'badge-danger'
        }`}>
          {generic.review_status?.replace('_', ' ')}
        </span>
      </div>
      <div className="w-20 text-right">
        <Link 
          to={`/drug/${generic.reference_drug_id}`}
          className="text-sm text-[#0A2540] hover:underline"
        >
          View
        </Link>
      </div>
    </div>
  );
}

export default function GeographyPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(searchParams.get('region') || 'US');
  const [regionData, setRegionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchRegions() {
      try {
        const res = await getRegions();
        setRegions(res.data.regions || []);
      } catch (error) {
        console.error('Failed to fetch regions:', error);
      }
    }
    fetchRegions();
  }, []);

  useEffect(() => {
    async function fetchRegionData() {
      if (!selectedRegion) return;
      setLoading(true);
      try {
        const res = await getRegionData(selectedRegion);
        setRegionData(res.data);
      } catch (error) {
        console.error('Failed to fetch region data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchRegionData();
  }, [selectedRegion]);

  const handleRegionSelect = (code) => {
    setSelectedRegion(code);
    setSearchParams({ region: code });
  };

  const currentRegion = regions.find(r => r.code === selectedRegion);

  return (
    <div className="p-6" data-testid="geography-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">Geographic Intelligence</h1>
        <p className="text-slate-500">Explore generic availability and market data by region</p>
      </div>

      {/* Region Selector */}
      <div className="mb-6">
        <h2 className="font-semibold text-slate-900 mb-4">Select Market</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {regions.map(region => (
            <RegionCard
              key={region.code}
              region={region}
              selected={selectedRegion === region.code}
              onClick={handleRegionSelect}
            />
          ))}
        </div>
      </div>

      {/* Region Overview */}
      {currentRegion && (
        <div className="card-premium p-6 mb-6" data-testid="region-overview">
          <div className="flex items-center gap-4 mb-4">
            <span className="text-4xl">{regionFlags[currentRegion.code] || '🌍'}</span>
            <div>
              <h2 className="text-xl font-bold text-slate-900">{currentRegion.name}</h2>
              <p className="text-slate-500">Regulatory Body: {currentRegion.regulatory_body}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 text-slate-500 mb-2">
                <Package className="w-4 h-4" />
                <span className="text-sm">Available Generics</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{regionData?.generics_count || 0}</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 text-slate-500 mb-2">
                <Building2 className="w-4 h-4" />
                <span className="text-sm">Active Manufacturers</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {[...new Set(regionData?.generics?.map(g => g.manufacturer) || [])].length}
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 text-slate-500 mb-2">
                <Globe className="w-4 h-4" />
                <span className="text-sm">Currency</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{currentRegion.currency}</p>
            </div>
          </div>
        </div>
      )}

      {/* Generics Table */}
      <div className="card-premium overflow-hidden" data-testid="generics-table">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">
            Available Generics in {currentRegion?.name || 'Selected Region'}
          </h3>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-slate-200 border-t-[#0A2540] rounded-full mx-auto" />
            <p className="text-slate-500 mt-4">Loading region data...</p>
          </div>
        ) : (
          <>
            <div className="flex items-center py-3 px-4 bg-slate-50 border-b border-slate-200">
              <div className="flex-1 text-xs font-medium text-slate-500 uppercase tracking-wider">Generic</div>
              <div className="w-24 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Form</div>
              <div className="w-24 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Strength</div>
              <div className="w-32 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Confidence</div>
              <div className="w-24 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Status</div>
              <div className="w-20 text-right text-xs font-medium text-slate-500 uppercase tracking-wider"></div>
            </div>
            
            {regionData?.generics?.length > 0 ? (
              regionData.generics.map(generic => (
                <GenericRow key={generic.id} generic={generic} />
              ))
            ) : (
              <div className="py-12 text-center">
                <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No generics available in this region</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
